// Centralized error parsing utilities (strict TS, no any)
// Converts unknown API/runtime errors into a safe, typed shape for UI

export interface ParsedErrorDetails {
  title: string;
  message: string;
  code?: string;
}

// Narrow shape for typical backend error format provided in the prompt
interface BackendErrorShape {
  errorTitle?: string;
  errorMessage?: string;
  code?: string;
}

// Minimal Axios-like error shape used for type guards
interface AxiosLikeError {
  isAxiosError?: boolean;
  message?: string;
  code?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

const isObject = (val: unknown): val is Record<string, unknown> => {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
};

const parseBackendShape = (data: unknown): ParsedErrorDetails | null => {
  if (!isObject(data)) return null;
  const candidate = data as BackendErrorShape;
  if (typeof candidate.errorTitle === 'string' || typeof candidate.errorMessage === 'string') {
    return {
      title: candidate.errorTitle || 'Request Failed',
      message: candidate.errorMessage || 'An unknown error occurred.',
      code: candidate.code,
    };
  }
  return null;
};

export const parseUnknownError = (err: unknown): ParsedErrorDetails => {
  // Unwrap common wrapper shape { error: originalError }
  if (isObject(err) && 'error' in err) {
    const inner = (err as Record<string, unknown>).error;
    if (inner) {
      return parseUnknownError(inner as unknown);
    }
  }

  // 1) Axios error with structured response
  const ax = err as AxiosLikeError;
  if (ax && isObject(ax)) {
    // Prefer backend payload details when available
    if (ax.response && ax.response.data !== undefined) {
      const parsed = parseBackendShape(ax.response.data);
      if (parsed) return parsed;
    }

    // Axios error but no backend shape
    if (ax.isAxiosError) {
      const status = ax.response?.status;
      const baseTitle = status ? `Request failed (${status})` : 'Request failed';
      return {
        title: baseTitle,
        message: typeof ax.message === 'string' && ax.message.trim().length > 0
          ? ax.message
          : 'Something went wrong. Please try again.',
        code: ax.code,
      };
    }
  }

  // 2) Plain object error with backend-like shape
  const backend = parseBackendShape(err as Record<string, unknown>);
  if (backend) return backend;

  // 3) Native Error instance
  if (err instanceof Error) {
    return {
      title: 'Error',
      message: err.message || 'Something went wrong. Please try again.',
    };
  }

  // 4) Fallback
  return {
    title: 'Something went wrong',
    message: 'Please try again.',
  };
};


